/**
 * Deals Page — kanban board or list view with pipeline selector.
 */

import { useEffect, useState, useCallback } from 'react'
import { LayoutGrid, List, Plus } from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { DealKanban } from '@/components/crm/DealKanban'
import { DealListView } from '@/components/crm/DealListView'
import { DealCreateModal } from '@/components/crm/DealCreateModal'
import { DealDetail } from '@/components/crm/DealDetail'
import { Button } from '@/components/ui'

export function DealsPage() {
  const { fetchPipelines, fetchDeals, fetchPipelineStats } = useCRM()
  const pipelines = useCrmStore((s) => s.pipelines)
  const activePipeline = useCrmStore((s) => s.activePipeline)
  const viewMode = useCrmStore((s) => s.dealViewMode)
  const setViewMode = useCrmStore((s) => s.setDealViewMode)
  const setActivePipeline = useCrmStore((s) => s.setActivePipeline)
  const selectedDeal = useCrmStore((s) => s.selectedDeal)

  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    fetchPipelines()
  }, [fetchPipelines])

  useEffect(() => {
    if (activePipeline) {
      fetchDeals(activePipeline.id)
      fetchPipelineStats(activePipeline.id)
    }
  }, [activePipeline, fetchDeals, fetchPipelineStats])

  const handlePipelineChange = useCallback((pipelineId: string) => {
    const pipeline = pipelines.find((p) => p.id === pipelineId)
    if (pipeline) setActivePipeline(pipeline)
  }, [pipelines, setActivePipeline])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border">
        {/* Pipeline selector + view toggle + add button */}
        <div className="py-3 flex items-center gap-3">
          {pipelines.length > 1 && (
            <select
              value={activePipeline?.id || ''}
              onChange={(e) => handlePipelineChange(e.target.value)}
              className="text-sm bg-muted border border-border rounded-lg px-3 py-2 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
            >
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          {pipelines.length <= 1 && activePipeline && (
            <span className="text-sm font-medium text-foreground">{activePipeline.name}</span>
          )}

          <div className="flex-1" />

          {/* View toggle */}
          <div className="flex border border-border rounded-lg overflow-hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('kanban')}
              leftIcon={<LayoutGrid className="w-3.5 h-3.5" />}
              className={`rounded-none ${
                viewMode === 'kanban'
                  ? 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary'
                  : 'text-muted-foreground'
              }`}
            >
              Board
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('list')}
              leftIcon={<List className="w-3.5 h-3.5" />}
              className={`rounded-none ${
                viewMode === 'list'
                  ? 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary'
                  : 'text-muted-foreground'
              }`}
            >
              List
            </Button>
          </div>

          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreateModal(true)}>
            Add Deal
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        <div className={`flex-1 overflow-auto ${selectedDeal ? 'hidden lg:block' : ''}`}>
          {!activePipeline ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground">
              <div className="animate-pulse">Loading pipeline...</div>
            </div>
          ) : viewMode === 'kanban' ? (
            <DealKanban pipeline={activePipeline} />
          ) : (
            <DealListView pipeline={activePipeline} />
          )}
        </div>

        {/* Deal detail slide-out */}
        {selectedDeal && (
          <div className="w-full lg:w-96 border-l border-border overflow-y-auto bg-background shrink-0">
            <DealDetail deal={selectedDeal} pipeline={activePipeline} />
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && activePipeline && (
        <DealCreateModal
          pipeline={activePipeline}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  )
}
